import { action } from '@ember/object';
import Component from '@glimmer/component';

import { ShownLabels } from 'webapp/enums/enums';

// this can be included block-wide (no dataSelect) or per data-select
import FormCheckboxSwitchComponent from 'webapp/components/form/checkbox-switch';
import FormRadiobuttonGroupComponent from 'webapp/components/form/radiobutton-group';

import type Block from 'webapp/models-custom/block';
import type BlockSetup from 'webapp/models-custom/block-setup';
import type DataSelect from 'webapp/models-custom/data-select';

interface Args {
	blockM: Block;
	dataSelect?: DataSelect;
}

export default class BlockSettingsMetricShowLabelsComponent extends Component<Args> {
	get block(): Block {
		return this.args.blockM;
	}

	get blockSetup(): BlockSetup {
		return this.block.blockSetup;
	}

	get visualization() {
		return this.blockSetup.visualization;
	}

	get isForSingleDataSelect(): boolean {
		return !!this.args.dataSelect;
	}

	get enabledOptionsForVisualization() {
		return this.visualization.enabledShownLabelsOptions;
	}

	get isSingleOption() {
		// if single, we show just toggle on/off
		// if multiple, we'll show radiobutton group
		return this.enabledOptionsForVisualization.length === 1;
	}

	// --- for single toggle ---

	get singleSelected(): boolean {
		return this.isForSingleDataSelect
			? !!this.args.dataSelect?.shownLabels
			: this.blockSetup.dataSelectsShown.every((ds) => ds.shownLabels);
	}

	@action
	changeSingleSelected(showLabels: boolean): void {
		const dataSelectsToUpdate = this.args.dataSelect
			? [this.args.dataSelect]
			: this.blockSetup.dataSelectsShown;

		dataSelectsToUpdate.forEach((ds) => {
			ds.shownLabels = showLabels
				? this.enabledOptionsForVisualization[0]!.value
				: ShownLabels.NONE;
			this.blockSetup.trackBlockPropertyMetricSettingsChangeEvent(ds, {
				show_values: showLabels,
			});
			this.blockSetup.saveDataSelect(ds);
		});
	}

	// --- for radiobutton group ---

	get selected(): ShownLabels {
		const selected = this.isForSingleDataSelect
			? this.args.dataSelect?.shownLabels
			: this.blockSetup.dataSelectsShown[0]?.shownLabels;
		return selected || ShownLabels.NONE;
	}

	get options() {
		const options = [
			{
				value: ShownLabels.NONE,
				label: 'Off',
			},
		].concat(this.enabledOptionsForVisualization);
		return options;
	}

	@action
	changeSelected(value: AnySimpleValue): void {
		const shownLabels = value as ShownLabels;
		const dataSelectsToUpdate = this.args.dataSelect
			? [this.args.dataSelect]
			: this.blockSetup.dataSelectsShown;

		dataSelectsToUpdate.forEach((ds) => {
			ds.shownLabels = shownLabels;
			this.blockSetup.trackBlockPropertyMetricSettingsChangeEvent(ds, {
				show_values: shownLabels !== ShownLabels.NONE,
			});
			this.blockSetup.saveDataSelect(ds);
		});
	}
	<template>
		{{#if this.isSingleOption}}
			<div class="row row--toggle">
				<FormCheckboxSwitchComponent
					@name="showValues"
					@selected={{this.singleSelected}}
					@onChange={{this.changeSingleSelected}}
				/>
				<label>Show values</label>
			</div>
		{{else}}
			<div class="row">
				<label>Show values</label>
				<FormRadiobuttonGroupComponent
					class="radiobutton-switcher"
					@name="aggregation"
					@selected={{this.selected}}
					@options={{this.options}}
					@onChange={{this.changeSelected}}
					@theme="dark-blue"
				/>
			</div>
		{{/if}}
	</template>
}
